import { useCallback, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import "./AcademicRecordSheet.css";

type SheetWidth = "standard" | "wide";

/**
 * Moving through the list without closing the sheet.
 *
 * A reviewer working a queue of applications otherwise closes the record,
 * finds their place in the list behind it, and opens the next one — three acts
 * to perform the one they meant. The sheet takes the two ends of the list
 * rather than the list itself: it has no business knowing what a record is, and
 * a page that supplies `null` for either end says "there is nothing that way",
 * which is the only fact the control needs.
 */
export interface RecordQueueNavigation {
  previousId: string | null;
  nextId: string | null;
  onNavigate: (id: string) => void;
  /** "applicant 3 of 47". Omitted where position would mislead. */
  position?: string;
}

interface AcademicRecordSheetProps {
  open: boolean;
  onClose: () => void;
  eyebrow: string;
  title: string;
  description: string;
  status?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: SheetWidth;
  dirty?: boolean;
  onDiscard?: () => void;
  discardDescription?: string;
  queue?: RecordQueueNavigation;
  focusHeading?: boolean;
  onCaptureOpener?: (element: HTMLElement | null) => void;
  onRestoreFocus?: () => void;
  /** Task-focused course composition retains the same record/focus owner. */
  appearance?: "record" | "coursework";
}

export function AcademicRecordSheet({
  open,
  onClose,
  eyebrow,
  title,
  description,
  status,
  actions,
  children,
  footer,
  width = "standard",
  dirty = false,
  onDiscard,
  discardDescription = "You have unsaved changes in this record. Discard them and close the record?",
  queue,
  focusHeading = false,
  onCaptureOpener,
  onRestoreFocus,
  appearance = "record",
}: AcademicRecordSheetProps) {
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const heading = useRef<HTMLHeadingElement | null>(null);

  // Stepping to the next record leaves this one exactly as closing it would, so
  // it goes through the same guard. Discarding an unsaved committee note by
  // pressing "next" would be the same loss with none of the warning.
  const requestNavigate = useCallback((id: string) => {
    if (dirty) {
      setPendingId(id);
      setConfirmDiscard(true);
      return;
    }
    queue?.onNavigate(id);
  }, [dirty, queue]);

  const requestClose = useCallback(() => {
    if (dirty) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  }, [dirty, onClose]);

  const discardAndContinue = useCallback(() => {
    onDiscard?.();
    setConfirmDiscard(false);
    if (pendingId) {
      const id = pendingId;
      setPendingId(null);
      queue?.onNavigate(id);
      return;
    }
    onClose();
  }, [onClose, onDiscard, pendingId, queue]);

  return (
    <>
      <Sheet open={open} onOpenChange={(nextOpen) => !nextOpen && requestClose()}>
        <SheetContent
          data-record-appearance={appearance}
          data-record-width={width}
          side="right"
          hideDefaultTitle
          // The record that opened the sheet is where the keyboard has to come
          // back to. A modal `DialogContent` refuses the focus scope's own
          // restore and focuses `DialogTrigger` instead — and this sheet has no
          // trigger, because every consumer opens it from a row through a
          // controlled `open`. So the default focused nothing and left the
          // reader on `<body>`: the next Tab started again from the top of the
          // document, several pages above the record they had just closed.
          //
          // Taken here rather than in an effect, because a child's effects run
          // before its parent's: by the time this component's effect could look,
          // the focus scope has already moved focus inside the sheet.
          onOpenAutoFocus={(event) => {
            opener.current = document.activeElement as HTMLElement | null;
            onCaptureOpener?.(opener.current);
            if (focusHeading) { event.preventDefault(); heading.current?.focus(); }
          }}
          onCloseAutoFocus={(event) => {
            if (onRestoreFocus) { event.preventDefault(); onRestoreFocus(); return; }
            // A row the reader filtered away while the sheet was open is not
            // somewhere to send them. Radix's own fallback handles that.
            if (!opener.current?.isConnected) return;
            event.preventDefault();
            opener.current.focus();
          }}
          className={cn(
            "w-full gap-0 rounded-none border-l border-border bg-background p-0 sm:rounded-l-xl",
            appearance === "coursework" && "coursework-record",
            width === "wide"
              ? "sm:max-w-[var(--academic-drawer-width-wide)]"
              : "sm:max-w-[var(--academic-drawer-width)]",
          )}
        >
          <SheetHeader className="grid gap-2 border-b border-rule px-5 pb-5 pt-6 sm:px-7 sm:pb-6 sm:pt-7">
            <p className="eyebrow pr-12">{eyebrow}</p>
            <div className="flex min-w-0 flex-wrap items-start justify-between gap-3 pr-10">
              <SheetTitle ref={heading} tabIndex={focusHeading ? -1 : undefined} className="min-w-0 flex-1 [overflow-wrap:anywhere] text-tile-title font-semibold text-foreground">
                {title}
              </SheetTitle>
              {status && <div className="flex shrink-0 flex-wrap items-center gap-2">{status}</div>}
            </div>
            <SheetDescription className="max-w-2xl text-meta text-muted-foreground">
              {description}
            </SheetDescription>
            {(actions || queue) && (
              <div className="flex flex-wrap items-center gap-2 pt-2">
                {actions}
                {queue && (
                  <div className="ml-auto flex items-center gap-1.5">
                    {queue.position && (
                      <span className="mr-1 font-data text-meta text-muted-foreground">{queue.position}</span>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="icon-sm"
                      aria-label="Previous record"
                      disabled={!queue.previousId}
                      onClick={() => queue.previousId && requestNavigate(queue.previousId)}
                    >
                      <ChevronLeft strokeWidth={1.75} aria-hidden="true" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon-sm"
                      aria-label="Next record"
                      disabled={!queue.nextId}
                      onClick={() => queue.nextId && requestNavigate(queue.nextId)}
                    >
                      <ChevronRight strokeWidth={1.75} aria-hidden="true" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </SheetHeader>

          <SheetBody className="min-h-0 p-0">
            <div className="academic-record-body-inner grid min-h-full content-start gap-4 px-5 py-6 sm:px-7 sm:py-7">
              {children}
            </div>
          </SheetBody>

          {footer && (
            <SheetFooter className="border-t border-rule bg-background px-5 py-4 sm:px-7">
              {footer}
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={confirmDiscard}
        onOpenChange={(next) => {
          setConfirmDiscard(next);
          if (!next) setPendingId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>{discardDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={discardAndContinue}>
              {pendingId ? "Discard and continue" : "Discard and close"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function AcademicRecordSection({
  eyebrow,
  title,
  description,
  aside,
  children,
  className,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("grid gap-4 border-b border-rule pb-4 last:border-b-0 last:pb-0", className)}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="grid min-w-0 gap-1">
          {eyebrow && <p className="eyebrow">{eyebrow}</p>}
          <h3 className="text-section font-semibold text-foreground">{title}</h3>
          {description && <p className="max-w-xl text-ui text-muted-foreground">{description}</p>}
        </div>
        {aside && <div className="min-w-0 max-w-full shrink-0">{aside}</div>}
      </div>
      {children}
    </section>
  );
}

export interface AcademicRecordFact {
  label: string;
  value: ReactNode;
  span?: 1 | 2;
}

export function AcademicRecordFacts({ items, className }: { items: AcademicRecordFact[]; className?: string }) {
  return (
    <dl className={cn("grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2", className)}>
      {items.map((item) => (
        <div key={item.label} className={cn("grid gap-1", item.span === 2 && "sm:col-span-2")}>
          <dt className="eyebrow">{item.label}</dt>
          <dd className="text-ui text-foreground">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
